"use client";

import { Logo } from "@/components/xui/logo";
import { FaUserCircle } from "react-icons/fa";
import { useAuth, useSignOut } from "@/lib/hooks/use-auth";
import Link from "next/link";
import Image from "next/image";
import { SearchInput } from "@/components/shared/search-input";
import { IoCaretDownOutline, IoSettings } from "react-icons/io5";
import { TbGridDots } from "react-icons/tb";
import { BsFillChatTextFill } from "react-icons/bs";
import { DropdownMenuSuite } from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useState } from "react";
import { HOME_PAGE, ROUTES } from "@/lib/constants";
import { navItems } from "./sidebar";
import { NotificationBell } from "@padi/shared/notifications";

export default function DashboardHeader() {
  const { user } = useAuth();
  const { signOut } = useSignOut();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { name: "Dashboard", href: HOME_PAGE, icon: TbGridDots },
    { name: "Support", href: ROUTES.SETTINGS, icon: BsFillChatTextFill },
    { name: "Settings", href: ROUTES.SETTINGS, icon: IoSettings },
  ];

  const dropdownLinks = [
    // { name: "View Profile", href: ROUTES.PROFILE },
    { name: "Settings", href: ROUTES.SETTINGS },
    { name: "Logout", href: "#", action: signOut },
  ];

  const DropdownMenuTrigger = (
    <button className="flex items-center gap-2 md:gap-3 outline-none">
      <div className="hidden md:block text-left">
        <p className="font-bold text-foreground">{user?.displayName}</p>
        <p className="text-xs text-gray-600">
          User id: {user?.id?.slice(0, 8)}
        </p>
      </div>
      <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-200 flex items-center justify-center relative overflow-hidden">
        {user?.avatarUrl ? (
          <Image
            src={user.avatarUrl}
            alt={user.displayName || "User"}
            fill
            className="object-cover"
          />
        ) : (
          <FaUserCircle className="text-xl md:text-2xl text-gray-400" />
        )}
      </div>
      <IoCaretDownOutline className="text-sm hidden md:block" />
    </button>
  );

  return (
    <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4 font-pt-sans xl:text-lg">
      <div className="flex items-center">
        {/* Left Section - Logo */}
        <div className="flex items-center gap-4">
          <Logo />
          {/* Desktop Search Bar */}
          <SearchInput
            variant="dashboard"
            className="hidden lg:block ml-4 xl:ml-9 min-w-48"
          />
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center ml-auto gap-6 w-1/3 justify-center">
          {navLinks.map((link) => (
            <Link
              href={link.href}
              key={link.name}
              className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
            >
              <link.icon className="text-xl" />
              <span className="font-medium">{link.name}</span>
            </Link>
          ))}
        </div>

        {/* Right Section - Notifications & User Profile */}
        <div className="flex items-center gap-3 md:gap-6 ml-auto">
          {/* Notifications */}
          <NotificationBell />

          {/* User Dropdown */}
          <DropdownMenuSuite
            trigger={DropdownMenuTrigger}
            contentClassName="w-44 bg-primary text-white rounded border border-gray-200"
          >
            {dropdownLinks.map((link) => (
              <Link
                href={link.href}
                key={link.name}
                className="block px-4 py-2 hover:opacity-80"
                onClick={link.action}
              >
                {link.name}
              </Link>
            ))}
          </DropdownMenuSuite>

          {/* Mobile Menu Toggle */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button className="p-2 hover:bg-gray-100 rounded-md transition-colors">
                <Menu className="h-6 w-6 text-gray-600" />
              </button>
            </SheetTrigger>
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetContent side="right" className="w-[280px] sm:w-[350px] p-3">
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold">Menu</h2>
                </div>

                {/* Mobile Search */}
                <div className="mb-6 lg:hidden">
                  <SearchInput variant="dashboard" className="w-full" />
                </div>

                {/* Mobile Navigation Links */}
                <nav className="flex flex-col gap-2 mb-6">
                  {[...navItems, navLinks[1]].map((link) => (
                    <Link
                      href={link.href}
                      key={link.name}
                      className="flex items-center gap-3 p-3 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <link.icon className="text-xl" />
                      <span className="font-medium">{link.name}</span>
                    </Link>
                  ))}
                </nav>

                {/* Mobile User Info */}
                <div className="mt-auto pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center relative overflow-hidden">
                      {user?.avatarUrl ? (
                        <Image
                          src={user.avatarUrl}
                          alt={user.displayName || "User"}
                          fill
                          className="object-cover"
                        />
                      ) : (
                        <FaUserCircle className="text-3xl text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-bold text-foreground">
                        {user?.displayName}
                      </p>
                      <p className="text-xs text-gray-600">
                        User id: {user?.id?.slice(0, 8)}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Quick Links */}
                  <div className="flex flex-col gap-2">
                    {dropdownLinks.map((link) => (
                      <Link
                        href={link.href}
                        key={link.name}
                        className="block px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        onClick={(e) => {
                          if (link.action) {
                            e.preventDefault();
                            link.action();
                          }
                          setMobileMenuOpen(false);
                        }}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
